/**
 * Окно «Добавить платье» страницы «Генерация» (06.10.26): папки «Наших фото» плиткой — обложка, имя,
 * число фото, регионы, — а не список имён. Фильтры «отправитель» и «регион» (как в `/photos`), поиск по
 * имени, бейдж «уже было с #N». Фишка «ещё не генерировали с референсом» — здесь же: прячет папки, с
 * которыми уже генерировали по ВСЕМ референсам страницы.
 *
 * Строка «Похожие на сцену» (если странице передали `scenes`): выбрать сцену, «Найти» — платья встают по
 * сходству с ней, на обложке процент; «по имени» возвращает обычный порядок. Выдача живёт, пока не нажали «Найти» снова.
 *
 * Отмечаются несколько папок, «Добавить N» отдаёт их странице в порядке отметок. Уже стоящие в генерации —
 * отмечены и не снимаются.
 */
import { Check, ImageOff } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Dialog } from "@/components/trends/Dialog";
import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnPrimary, Chip, Empty, ErrorLine, input } from "@/components/trends/ui";
import { orderDresses, sceneKey, type DressSimilar, type SceneRef } from "@/lib/dressSimilar";
import { mediaUrl, num } from "@/lib/media";
import { dressTitle, searchDressesByScene, surname, type DressCard, type PhotosList } from "@/lib/photos";
import { scoreLabel } from "@/lib/similar";

export function DressPickerDialog({
  open,
  onClose,
  list,
  taken,
  annotate,
  triedAll,
  scenes,
  single = false,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  list: PhotosList | undefined;
  /** Платья, что уже в генерации. */
  taken: readonly number[];
  /** Бейдж после имени: «уже было с #5». */
  annotate: ReadonlyMap<number, string>;
  /** Платья, с которыми генерировали по всем референсам страницы; `null` — референсов нет, фишки нет. */
  triedAll: ReadonlySet<number> | null;
  /** Сцены страницы (пины, затем референсы); нет — строки «Похожие на сцену» нет, пусто — подсказка. */
  scenes?: readonly SceneRef[] | undefined;
  /** Одиночный выбор: клик по папке сразу отдаёт её и закрывает окно (платье одно на действие). */
  single?: boolean;
  onAdd: (ids: number[]) => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Добавить платье" wide>
      <Picker list={list} taken={taken} annotate={annotate} triedAll={triedAll} scenes={scenes} single={single} onAdd={onAdd} onClose={onClose} />
    </Dialog>
  );
}

function Picker({
  list,
  taken,
  annotate,
  triedAll,
  scenes,
  single,
  onAdd,
  onClose,
}: {
  list: PhotosList | undefined;
  taken: readonly number[];
  annotate: ReadonlyMap<number, string>;
  triedAll: ReadonlySet<number> | null;
  scenes?: readonly SceneRef[] | undefined;
  single?: boolean;
  onAdd: (ids: number[]) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [sender, setSender] = useState<number | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [notYet, setNotYet] = useState(true);
  const [sceneSel, setSceneSel] = useState<string | null>(null);
  const [similar, setSimilar] = useState<DressSimilar | null>(null);
  const find = useMutation({ mutationFn: searchDressesByScene, onSuccess: setSimilar });
  const scene = scenes?.find((s) => sceneKey(s) === sceneSel) ?? scenes?.[0];
  const scores = useMemo(() => (similar ? new Map(similar.dresses.map((x) => [x.dress_id, x.score])) : null), [similar]);

  const needle = query.trim().toLowerCase();
  const dresses = list?.dresses;
  const shown = useMemo(
    () =>
      orderDresses(
        (dresses ?? []).filter(
        (d) =>
          (!needle || dressTitle(d).toLowerCase().includes(needle)) &&
          (sender === null || d.senders.some((s) => s.user_id === sender)) &&
          (region === null || d.regions.includes(region)) &&
          !(triedAll && notYet && triedAll.has(d.id) && !taken.includes(d.id)),
        ),
        scores,
      ),
    [dresses, needle, sender, region, triedAll, notYet, taken, scores],
  );

  const toggle = (id: number) => {
    if (single) {
      onAdd([id]);
      onClose();
      return;
    }
    setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  return (
    <>
      <div className="grid gap-2 border-b border-border px-4 py-2" data-annot="фильтры окна платьев">
        <div className="flex flex-wrap items-center gap-2">
          <input
            autoFocus
            className={`${input} w-[260px] max-w-full`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={160}
            placeholder="найти платье по имени…"
          />
          {triedAll ? (
            <Chip
              on={notYet}
              onClick={() => setNotYet((v) => !v)}
              title="Скрыть платья, с которыми уже генерировали по всем референсам страницы"
            >
              ещё не генерировали с референсом
            </Chip>
          ) : null}
          <span className="text-[12px] text-fg-faint tnum">
            {num(shown.length)} из {num(dresses?.length ?? 0)}
          </span>
        </div>
        {scenes ? (
          <div className="flex flex-wrap items-center gap-1.5" data-annot="похожие на сцену">
            <span className="text-[11.5px] text-fg-faint">Похожие на сцену</span>
            <select
              className={`${input} max-w-[320px]`}
              disabled={scenes.length === 0}
              value={scene ? sceneKey(scene) : ""}
              onChange={(e) => setSceneSel(e.target.value)}
            >
              {scenes.map((s) => (
                <option key={sceneKey(s)} value={sceneKey(s)}>
                  {s.kind === "pin" ? "пин" : "референс"} #{s.id} · {s.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={btn}
              disabled={!scene || find.isPending}
              onClick={() => scene && find.mutate(scene)}
            >
              {find.isPending ? "Ищу…" : "Найти"}
            </button>
            {scenes.length === 0 ? <span className="text-[12px] text-fg-faint">сначала добавьте референс</span> : null}
            {similar ? (
              <>
                <Chip
                  on={false}
                  onClick={() => {
                    setSimilar(null);
                    find.reset();
                  }}
                  title="Вернуть обычный порядок папок"
                >
                  по имени
                </Chip>
                {similar.missing > 0 ? (
                  <span className="text-[12px] text-fg-faint tnum">{num(similar.missing)} фото без вектора</span>
                ) : null}
              </>
            ) : null}
            {find.isError ? <ErrorLine error={find.error} /> : null}
          </div>
        ) : null}
        {list && list.senders.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11.5px] text-fg-faint">отправитель</span>
            <Chip on={sender === null} onClick={() => setSender(null)}>
              все
            </Chip>
            {list.senders.map((s) => (
              <Chip
                key={s.user_id}
                on={sender === s.user_id}
                title={s.name}
                onClick={() => setSender(sender === s.user_id ? null : s.user_id)}
              >
                {surname(s.name)}
              </Chip>
            ))}
          </div>
        ) : null}
        {list && list.regions.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11.5px] text-fg-faint">регион</span>
            <Chip on={region === null} onClick={() => setRegion(null)}>
              все
            </Chip>
            {list.regions.map((r) => (
              <Chip key={r.short} on={region === r.short} onClick={() => setRegion(region === r.short ? null : r.short)}>
                {r.short}
              </Chip>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex-1">
        {!dresses ? (
          <Empty>Загружаю папки…</Empty>
        ) : shown.length === 0 ? (
          <Empty>{dresses.length ? "Под фильтр не попала ни одна папка." : "Папок с фото пока нет."}</Empty>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 p-4">
            {shown.map((d) => (
              <DressTile
                key={d.id}
                d={d}
                taken={taken.includes(d.id)}
                on={picked.includes(d.id)}
                badge={annotate.get(d.id) ?? null}
                score={scores?.get(d.id)}
                onToggle={() => toggle(d.id)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-bg px-4 py-2.5">
        <span className="mr-auto text-[12px] text-fg-faint">
          {single ? "клик по папке выбирает платье" : picked.length ? `отмечено: ${picked.length}` : "клик по папке отмечает; уже добавленные отмечены"}
        </span>
        <button type="button" className={btn} onClick={onClose}>
          Отмена
        </button>
        {single ? null : (
        <button
          type="button"
          className={btnPrimary}
          disabled={picked.length === 0}
          onClick={() => {
            onAdd(picked);
            onClose();
          }}
        >
          Добавить {picked.length || ""}
        </button>
        )}
      </div>
    </>
  );
}

function DressTile({
  d,
  taken,
  on,
  badge,
  score,
  onToggle,
}: {
  d: DressCard;
  taken: boolean;
  on: boolean;
  badge: string | null;
  score?: number | undefined;
  onToggle: () => void;
}) {
  const title = dressTitle(d);
  const checked = taken || on;
  return (
    <button
      type="button"
      disabled={taken}
      aria-pressed={checked}
      onClick={onToggle}
      title={taken ? "уже в генерации" : title}
      className={`group grid content-start overflow-hidden rounded-[var(--radius-card)] border bg-bg-subtle text-left transition-colors ${
        checked ? "border-accent ring-1 ring-accent" : "border-border hover:border-border-strong"
      } ${taken ? "cursor-default opacity-60" : ""}`}
      data-annot="папка платья в окне"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-bg-elevated">
        {d.cover?.thumb_path ? (
          <RetryImg src={mediaUrl(d.cover.thumb_path)} alt={title} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="grid h-full place-items-center text-[12px] text-fg-faint">
            <span className="grid justify-items-center gap-1">
              <ImageOff size={18} />
              {d.cover ? "превью нет" : "пусто"}
            </span>
          </div>
        )}
        <span
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border ${
            checked ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white"
          }`}
        >
          {checked ? <Check size={13} strokeWidth={3} /> : null}
        </span>
        {score !== undefined ? (
          <span className="absolute top-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white tnum">
            {scoreLabel(score)}
          </span>
        ) : null}
        {taken ? (
          <span className="absolute bottom-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
            уже в генерации
          </span>
        ) : null}
      </div>
      <div className="grid gap-1 px-2.5 py-2">
        <div className={`truncate text-[13px] font-medium ${d.name ? "text-fg" : "text-fg-muted"}`}>{title}</div>
        <div className="text-[11.5px] text-fg-muted tnum">{num(d.count)} фото</div>
        {badge ? <div className="text-[11px] text-warn">{badge}</div> : null}
        {d.regions.length ? (
          <div className="flex flex-wrap gap-1">
            {d.regions.map((r) => (
              <span key={r} className="rounded-[3px] bg-bg-hover px-1 text-[10.5px] leading-[16px] text-fg-muted">
                {r}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </button>
  );
}
